import { Paragraph } from '@zillow/constellation-10';
import type { ComponentProps } from 'react';
import { useCachedLeadByConversationId } from '../../../../hooks/useLeadManagementTableQuery';
import TermsOfUse from '../../../TermsOfUse';
import { ACTION_TEXT } from '../../../../utils/getApplicationActionText';
import StringConstants from '../../../../constants/StringConstants';
import getRenderableNextStepLink from '../../../../utils/getRenderableNextStepLink';

type NextStepHelperTextProps = {
  conversationId: string;
  // The Renter Details Modal footer renders the button-style CTA, which has no action for the
  // tour-request step; the helper copy must disappear with it.
  isButtonStyle?: boolean;
  // Overrides the default size; the modal footer passes body-xs while the table/cards tooltip
  // keeps its default sizing.
  textStyle?: ComponentProps<typeof Paragraph>['textStyle'];
};

// Helper copy for a lead's next step, shared by the lead-management tooltip and the Renter Details
// Modal footer. Returns null when the next step needs no helper text.
export default function NextStepHelperText({
  conversationId,
  isButtonStyle = false,
  textStyle,
}: NextStepHelperTextProps) {
  const lead = useCachedLeadByConversationId(conversationId);
  const { nextStep, status } = lead || {};
  const { linkId } = nextStep?.link || {};
  const { isEnabled, tooltip } = nextStep || {};
  const { isConversationBlockedByZillow } = status || {};

  if (!getRenderableNextStepLink(lead, { isButtonStyle })) {
    return null;
  }

  if (isEnabled === false && tooltip) {
    return <Paragraph textStyle={textStyle}>{tooltip}</Paragraph>;
  }

  if (isConversationBlockedByZillow) {
    return <Paragraph textStyle={textStyle}>{StringConstants.TOOLTIP.BLOCKED_BY_ZILLOW}</Paragraph>;
  }

  if (isEnabled === false) {
    return <Paragraph textStyle={textStyle}>{StringConstants.TOOLTIP.UNAVAILABLE}</Paragraph>;
  }

  if (linkId === 'nextStepInviteToApply') {
    return <TermsOfUse actionText={ACTION_TEXT.INVITE_TO_APPLY} isInLeadManagement={true} textStyle={textStyle} />;
  }

  return null;
}
